// KONTRAK lintas-layar (pemilik: kelompok "spo"). Signature tetap.
// Dipakai: Daftar SPO, Beranda, pencarian global, Operasi. v1: A.openSpo(id) + verifyModal() di public/js/ops.js.
import { discountsFor, lineAmount, parseQty, unitsOf } from '@sj/shared';
import { useState } from 'react';
import { type Meta, productById, useSession } from '../../app/session.js';
import { useViewOrFallback } from '../../app/view.js';
import { Icon } from '../../components/Icon.js';
import { type ItemRow, ItemsTable } from '../../components/ItemsTable.js';
import { useConfirm, useModal } from '../../components/Modal.js';
import { SignBlock, type SignedFields, useSignModal } from '../../components/Signature.js';
import { useToast } from '../../components/Toast.js';
import { Pill } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { docHtml } from '../../lib/doc.js';
import { printHtml } from '../../lib/export.js';
import { fmtDT, num, qtyInput, rp, today } from '../../lib/format.js';
import { label } from '../../lib/status.js';
import { useOpenNota } from '../nota/open.js';

/** Detail SPO (GET /api/spo/:id → item). */
export interface SpoDetail extends SignedFields {
  id: number;
  no: string;
  date: string;
  status: string;
  source: string;
  store_id: number;
  store_name: string;
  address: string;
  store_phone: string;
  area: string;
  term_days: number;
  credit_limit: number;
  sales_id: number;
  sales_name: string;
  store_login: number;
  pay_plan: string;
  total: number;
  note: string;
  reject_reason: string;
  created_at: string;
  nota_id: number | null;
  nota_no: string | null;
  outstanding?: number;
}

/** Baris barang SPO (GET /api/spo/:id → items). */
export interface SpoItem {
  product_id: number;
  code: string;
  name: string;
  unit: string;
  qty: number;
  price: number;
  disc: number;
  base_unit: string;
  unit2: string;
  price2: number;
  unit2_factor: number;
  division: string;
  stock?: number;
  list_price: number;
}

// ---------- Verifikasi SPO → Nota (v1 verifyModal) ----------

interface VRow {
  product_id: number;
  unit: string;
  qty: number;
  price: number;
  disc: number;
}

interface VInfo {
  name: string;
  unit: string;
  stock?: number | null;
  division?: string | null;
  unitPrice: number;
  factor: number;
}

/** Status koreksi bersama (dipakai isi modal & submit), sama dengan variabel closure v1. */
interface VState {
  rows: VRow[];
  /** belum dikoreksi → diskon tetap seperti yang disetujui toko (sama dengan aturan server) */
  edited: boolean;
  gen: number;
  info: (r: VRow) => VInfo;
  discs: () => number[];
  total: () => number;
}

function verifyState(meta: Meta, items: SpoItem[]): VState {
  // Produk yang kini nonaktif tidak ada di meta.products → pakai data dari SPO.
  const info = (r: VRow): VInfo => {
    const p = productById(meta, r.product_id);
    const it = items.find((y) => y.product_id === r.product_id) || ({} as Partial<SpoItem>);
    const base = p || {
      name: it.name as string,
      unit: it.base_unit as string,
      price: it.list_price as number,
      stock: it.stock,
      division: it.division,
      unit2: it.unit2,
      price2: it.price2,
      unit2_factor: it.unit2_factor,
    };
    const u = unitsOf(base).find((y) => y.unit.toLowerCase() === String(r.unit).toLowerCase()) || {
      unit: r.unit,
      price: r.price,
      factor: 1,
    };
    return { ...base, unitPrice: u.price, factor: u.factor };
  };
  const v: VState = {
    rows: items.map((i) => ({ product_id: i.product_id, unit: i.unit, qty: i.qty, price: i.price, disc: i.disc || 0 })),
    edited: false,
    gen: 0,
    info,
    discs: () =>
      v.edited
        ? discountsFor(
            v.rows.map((r) => ({ ...r, division: info(r).division })),
            meta.discount_tiers,
          )
        : v.rows.map((r) => r.disc),
    total: () => {
      const ds = v.discs();
      return v.rows.reduce((a, r, i) => a + lineAmount(r.qty, r.price, ds[i]), 0);
    },
  };
  return v;
}

/** Potret baris saat digambar ulang penuh (v1 draw()): stok, harga master & diskon hanya diperbarui saat itu. */
const snapshot = (v: VState) => {
  v.gen += 1;
  return { gen: v.gen, rows: v.rows.map((r) => ({ ...r })), ds: v.discs() };
};

function VerifyBody({ x, v, meta }: { x: SpoDetail; v: VState; meta: Meta }) {
  const [snap, setSnap] = useState(() => snapshot(v));
  const [, setTick] = useState(0);
  const redraw = () => setSnap(snapshot(v));
  const ds = v.discs();
  const drivers = meta.drivers ?? [];
  return (
    <>
      {x.source === 'toko' ? (
        <div className="note">
          Order ini dipesan langsung oleh toko lewat aplikasi. Tentukan tanggal kirim &amp; driver di bawah.
        </div>
      ) : null}
      <dl className="kv">
        <dt>Toko</dt>
        <dd>
          {`${x.store_name} · ${x.area || ''}`}
          <div className="muted small">{x.address}</div>
        </dd>
        <dt>{x.source === 'toko' ? 'Sales PJ' : 'Sales'}</dt>
        <dd>{`${x.sales_name} · ${fmtDT(x.created_at)}`}</dd>
        {x.note ? (
          <>
            <dt>Catatan sales</dt>
            <dd>{x.note}</dd>
          </>
        ) : null}
        <dt>Piutang toko</dt>
        <dd>{`${rp(x.outstanding)}${x.credit_limit ? ` dari batas ${rp(x.credit_limit)}` : ''}`}</dd>
      </dl>
      <SignBlock x={x} />
      <div className="note info">
        {
          'Cocokkan qty & harga dengan pesanan yang disetujui toko. Mengurangi qty/harga boleh langsung (diskon skema dihitung ulang); '
        }
        <b>menambah produk, qty, atau menaikkan harga</b>
        {' akan mengembalikan order ke toko untuk tanda tangan ulang.'}
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Produk</th>
              <th>Qty</th>
              <th>Harga</th>
              <th className="right">Subtotal</th>
              <th />
            </tr>
          </thead>
          <tbody
            id="vRows"
            onInput={(e) => {
              const i = (e.target as HTMLElement).closest<HTMLInputElement>('[data-r]');
              if (!i?.dataset.k) return;
              const r = v.rows[Number(i.dataset.r)];
              if (!r) return;
              const val = i.dataset.k === 'qty' ? parseQty(i.value) : Math.floor(Number(i.value));
              r[i.dataset.k as 'qty' | 'price'] = Number.isFinite(val) && val >= 0 ? val : 0;
              v.edited = true;
              setTick((t) => t + 1);
            }}
          >
            {snap.rows.map((r, idx) => {
              const p = v.info(r);
              const short = p.stock != null && r.qty * p.factor > p.stock + 1e-9;
              const live = v.rows[idx] ?? r;
              return (
                <tr key={`${snap.gen}-${idx}`}>
                  <td>
                    {p.name || '?'}
                    <div className={`small ${short ? 'danger-txt' : 'muted'}`}>
                      {`Stok ${num(p.stock)} ${p.unit || ''}${short ? ' — kurang!' : ''}`}
                    </div>
                  </td>
                  <td>
                    <input
                      autoComplete="off"
                      className="inp qty"
                      inputMode="decimal"
                      data-r={idx}
                      data-k="qty"
                      defaultValue={qtyInput(r.qty)}
                    />
                    <div className="small muted">{r.unit}</div>
                  </td>
                  <td>
                    <input
                      autoComplete="off"
                      className="inp amt"
                      type="number"
                      min="0"
                      data-r={idx}
                      data-k="price"
                      defaultValue={r.price}
                    />
                    {p.unitPrice !== r.price ? <div className="small warn-txt">{`harga master ${rp(p.unitPrice)}`}</div> : null}
                    {snap.ds[idx] ? <div className="small ok-txt">{`diskon −${rp(snap.ds[idx])}/${r.unit}`}</div> : null}
                  </td>
                  <td className="right" data-sub={idx}>
                    {rp(lineAmount(live.qty, live.price, ds[idx]))}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="icon-btn"
                      data-m="del"
                      data-r={idx}
                      onClick={() => {
                        v.rows.splice(idx, 1);
                        v.edited = true;
                        redraw();
                      }}
                    >
                      <Icon n="x" cls="i-sm" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="row">
        <select
          className="inp grow"
          id="vAdd"
          defaultValue=""
          onChange={(e) => {
            const sel = e.currentTarget;
            if (!sel.value) return;
            const [pid, unit = ''] = sel.value.split('|');
            const p = productById(meta, pid);
            const u = p ? unitsOf(p).find((y) => y.unit === unit) : undefined;
            if (p && u && !v.rows.find((r) => r.product_id === p.id && r.unit.toLowerCase() === unit.toLowerCase()))
              v.rows.push({ product_id: p.id, unit: u.unit, qty: 1, price: u.price, disc: 0 });
            v.edited = true;
            sel.value = '';
            redraw();
          }}
        >
          <option value="">+ Tambah produk…</option>
          {meta.products.flatMap((p) =>
            unitsOf(p).map((u) => (
              <option key={`${p.id}|${u.unit}`} value={`${p.id}|${u.unit}`}>
                {`${p.name} — ${u.unit} (${rp(u.price)})`}
              </option>
            )),
          )}
        </select>
      </div>
      <div className="total-box">
        <span>Total tagihan</span>
        <span id="vTotal">{rp(v.total())}</span>
      </div>
      <div className="grid3">
        <label className="f">
          Tanggal kirim
          <input autoComplete="off" className="inp" type="date" name="ship_date" defaultValue={today()} />
        </label>
        <label className="f">
          Driver
          <select className="inp" name="driver_id" defaultValue="">
            <option value="">(pilih nanti)</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          Metode bayar
          <select className="inp" name="pay_method" defaultValue={x.pay_plan}>
            {['cash', 'transfer', 'tempo'].map((k) => (
              <option key={k} value={k}>
                {label('pm', k)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="f">
        Catatan nota
        <input autoComplete="off" className="inp" name="note" maxLength={300} />
      </label>
      {x.credit_limit ? (
        <label className="check">
          <input type="checkbox" name="override_limit" />
          {' Abaikan batas kredit (disetujui owner)'}
        </label>
      ) : null}
    </>
  );
}

/** Buka detail SPO (modal verifikasi/TTD) berdasarkan id. */
export function useOpenSpo(): (id: number) => Promise<void> {
  const { open } = useModal();
  const confirm = useConfirm();
  const sign = useSignModal();
  const toast = useToast();
  const { me, meta, isMgr, can, refreshCounts } = useSession();
  const openNota = useOpenNota();
  // v1 A.render(): muat ulang halaman aktif (di luar halaman, mis. pencarian global → muat ulang data halaman).
  const { reload: render } = useViewOrFallback();
  const showNota = (id: number) => void openNota(id).catch((e: Error) => toast(e.message, true));

  const verifyModal = (x: SpoDetail, items: SpoItem[]) => {
    const v = verifyState(meta, items);
    const m = open({
      wide: true,
      title: (
        <>
          Verifikasi SPO <span className="mono">{x.no}</span>
        </>
      ),
      body: <VerifyBody x={x} v={v} meta={meta} />,
      foot: (mm) => (
        <>
          <button
            type="button"
            className="btn btn-danger"
            data-m="reject"
            onClick={async (e) => {
              const btn = e.currentTarget;
              const reason = await confirm('Tolak SPO', `SPO ${x.no} akan ditolak dan sales melihat alasannya.`, {
                danger: true,
                ok: 'Tolak',
                input: 'Alasan penolakan',
              });
              if (!reason) return;
              await mm.run(async () => {
                await api('POST', `/api/spo/${x.id}/reject`, { reason });
                mm.close();
                toast('SPO ditolak.');
                render();
                void refreshCounts();
              }, btn);
            }}
          >
            Tolak SPO
          </button>
          <button type="submit" className="btn btn-primary">
            <Icon n="check" cls="i-sm" />
            {' Terbitkan Nota / Surat Jalan'}
          </button>
        </>
      ),
      submit: async (f) => {
        const rows = v.rows;
        if (!rows.length) throw new Error('Minimal satu produk.');
        const bad = rows.find((r) => !(r.qty > 0));
        if (bad) throw new Error(`Qty ${v.info(bad).name} harus lebih dari 0 (boleh pecahan, mis. 0,5).`);
        const el = (n: string) => f.elements.namedItem(n) as HTMLInputElement | null;
        const r = await api<{ id: number; no: string; needs_resign?: boolean }>('POST', `/api/spo/${x.id}/verify`, {
          items: rows.map(({ product_id, unit, qty, price }) => ({ product_id, unit, qty, price })),
          ship_date: el('ship_date')?.value,
          driver_id: el('driver_id')?.value || null,
          pay_method: el('pay_method')?.value,
          note: el('note')?.value,
          override_limit: el('override_limit') ? (el('override_limit') as HTMLInputElement).checked : false,
        });
        m.close();
        if (r.needs_resign) {
          toast(`SPO ${r.no} diubah — dikembalikan ke toko untuk tanda tangan ulang.`);
          void refreshCounts();
          render();
          return;
        }
        toast(`Nota ${r.no} terbit.`);
        void refreshCounts();
        render();
        showNota(r.id);
      },
    });
  };

  return async (id) => {
    let d: { item: SpoDetail; items: SpoItem[] };
    try {
      d = await api('GET', `/api/spo/${id}`);
    } catch (e) {
      toast((e as Error).message, true);
      return;
    }
    const { item: x, items } = d;
    if (can('spo.verifikasi') && x.status === 'diajukan' && x.signed_at) return verifyModal(x, items);
    const canSign = x.status === 'diajukan' && !x.signed_at && me.role === 'sales' && x.sales_id === me.id && can('spo.buat');
    const m = open({
      title: (
        <>
          SPO <span className="mono">{x.no}</span>
        </>
      ),
      body: (
        <>
          <dl className="kv">
            <dt>Toko</dt>
            <dd>{x.store_name}</dd>
            <dt>Tanggal</dt>
            <dd>{fmtDT(x.created_at)}</dd>
            <dt>Sales</dt>
            <dd>{x.sales_name}</dd>
            <dt>Rencana bayar</dt>
            <dd>
              <Pill kind="pm" v={x.pay_plan} />
            </dd>
            <dt>Status</dt>
            <dd>
              <Pill kind="spo" v={x.status} />
            </dd>
            {x.nota_no ? (
              <>
                <dt>Nota</dt>
                <dd className="mono">{x.nota_no}</dd>
              </>
            ) : null}
            {x.note ? (
              <>
                <dt>Catatan</dt>
                <dd>{x.note}</dd>
              </>
            ) : null}
            {x.reject_reason ? (
              <>
                <dt>Alasan tolak</dt>
                <dd className="danger-txt">{x.reject_reason}</dd>
              </>
            ) : null}
          </dl>
          <SignBlock x={x} />
          {canSign && x.store_login ? (
            <div className="small muted">Toko ini punya akun — toko juga bisa menyetujui dari aplikasinya sendiri.</div>
          ) : null}
          <ItemsTable items={items as unknown as ItemRow[]} qtyKey="qty" />
        </>
      ),
      foot: (mm) => (
        <>
          {x.status === 'diajukan' && !isMgr && can('spo.batal') ? (
            <button
              type="button"
              className="btn btn-danger"
              data-m="cancel"
              onClick={async (e) => {
                const btn = e.currentTarget;
                if (!(await confirm('Batalkan SPO?', `SPO ${x.no} akan dibatalkan.`, { danger: true }))) return;
                await mm.run(async () => {
                  await api('POST', `/api/spo/${x.id}/cancel`);
                  mm.close();
                  toast('SPO dibatalkan.');
                  render();
                }, btn);
              }}
            >
              Batalkan SPO
            </button>
          ) : null}
          {canSign ? (
            <button
              type="button"
              className="btn btn-primary"
              data-m="sign"
              onClick={() => {
                mm.close();
                sign({
                  title: (
                    <>
                      {'Persetujuan toko · '}
                      <span className="mono">{x.no}</span>
                    </>
                  ),
                  name: '',
                  summary: (
                    <>
                      <div className="total-box">
                        <span>{x.store_name}</span>
                        <span>{rp(x.total)}</span>
                      </div>
                      <ItemsTable items={items as unknown as ItemRow[]} qtyKey="qty" />
                    </>
                  ),
                  submit: async (body) => {
                    await api('POST', `/api/spo/${x.id}/sign`, body);
                    toast('Order disetujui toko — masuk antrian verifikasi admin.');
                    render();
                  },
                });
              }}
            >
              <Icon n="check" cls="i-sm" />
              {' Tanda tangan sekarang'}
            </button>
          ) : null}
          {x.nota_id && me.role !== 'driver' && can('nota.lihat') ? (
            <button
              type="button"
              className="btn"
              data-m="nota"
              onClick={() => {
                mm.close();
                showNota(x.nota_id as number);
              }}
            >
              Lihat nota
            </button>
          ) : null}
          <button
            type="button"
            className="btn"
            data-m="print"
            onClick={() =>
              printHtml(docHtml(meta, meta.settings.doc_title_spo ?? '', x.no, { ...x, store_address: x.address }, items, 'qty'))
            }
          >
            <Icon n="print" cls="i-sm" />
            {' Cetak'}
          </button>
          <button type="button" className="btn btn-primary" data-m="close" onClick={mm.close}>
            Tutup
          </button>
        </>
      ),
    });
    void m;
  };
}
